import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { create } from 'zustand';

export type League = 'bronze' | 'silver' | 'gold';

export interface Me {
  user: {
    id: number;
    firstName: string | null;
    username: string | null;
    photoUrl: string | null;
    isVip: boolean;
    ticketCode: string;
    onboarded: boolean;
    streakDays: number;
    subscribed: boolean;
    reveal: { day: number; total: number };
  };
  season: {
    id: number;
    name: string;
    prizeName: string;
    prizeImage: string | null;
    startsAt: string;
    endsAt: string;
    drawAt: string;
    isTest: boolean;
    vipReward: number;
    vipX: number;
  };
  /** sections the owner can hide in admin (wheel off → Support tab instead) */
  features: { wheel: boolean };
  balance: { season: number; permanent: number; total: number };
  rank: number | null;
  chancePct: number;
  participants: number;
  totalTickets: number;
  league: League;
  nextLeague: { league: League; need: number } | null;
  winners: {
    id: number;
    name: string;
    city: string | null;
    prize: string;
    mediaUrl: string | null;
  }[];
}

export interface BoardRow {
  userId: number;
  rank: number;
  tickets: number;
  name: string;
  city: string | null;
  photoUrl: string | null;
  isVip: boolean;
  league: League;
  chancePct: number;
}
export interface Board {
  top: BoardRow[];
  me: BoardRow | null;
  rival: { row: BoardRow; ticketsToPass: number } | null;
  participants: number;
  totalTickets: number;
  leaderTickets: number;
  leagues: { silver: number; gold: number };
}
export interface LedgerRow {
  id: number;
  action: string;
  amount: number;
  permanent: boolean;
  refType: string | null;
  createdAt: string;
}

interface Session {
  initData: string | null;
  token: string | null;
  expiresAt: number;
  setInitData: (v: string | null) => void;
}
export const useSession = create<Session>(() => ({
  initData: null,
  token: null,
  expiresAt: 0,
  setInitData: (initData) => useSession.setState({ initData, token: null, expiresAt: 0 }),
}));

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(code);
  }
}

let authing: Promise<string> | null = null;

/** initData → app JWT (15 min). Re-auth with the same initData when it expires. */
async function token(): Promise<string> {
  const s = useSession.getState();
  if (s.token && Date.now() < s.expiresAt - 30_000) return s.token;
  if (!s.initData) throw new ApiError(401, 'no_init_data');
  authing ??= (async () => {
    try {
      const res = await fetch('/api/app/auth', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ initData: s.initData }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(res.status, body.error ?? 'auth_failed');
      useSession.setState({ token: body.token, expiresAt: Date.now() + body.expiresInSec * 1000 });
      return body.token as string;
    } finally {
      authing = null;
    }
  })();
  return authing;
}

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const t = await token();
  const res = await fetch(path, {
    method: init.method ?? 'GET',
    headers: {
      authorization: `Bearer ${t}`,
      ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 401) useSession.setState({ token: null, expiresAt: 0 });
  if (!res.ok) throw new ApiError(res.status, body.error ?? String(res.status));
  return body as T;
}

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/api/app/me') });
export const useBoard = () =>
  useQuery({ queryKey: ['rank'], queryFn: () => api<Board>('/api/app/rank') });
export const useLedger = () =>
  useQuery({ queryKey: ['ledger'], queryFn: () => api<{ rows: LedgerRow[] }>('/api/app/ledger') });

/**
 * Live updates over WebSocket: a `balance` message flips the ticket (onAward) and
 * refreshes my numbers; `rank` refreshes the board. Reconnects with backoff.
 */
export function useLive(onAward: (amount: number) => void) {
  const qc = useQueryClient();
  const awardRef = useRef(onAward);
  useEffect(() => {
    awardRef.current = onAward;
  }, [onAward]);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let stopped = false;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const open = async () => {
      let t: string;
      try {
        t = await token();
      } catch {
        timer = setTimeout(open, 5000);
        return;
      }
      if (stopped) return;
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${proto}://${location.host}/api/ws?token=${encodeURIComponent(t)}`);
      ws.onopen = () => (retry = 0);
      ws.onmessage = (e) => {
        const msg = JSON.parse(String(e.data)) as { type: string; amount?: number };
        if (msg.type === 'balance') {
          void qc.invalidateQueries({ queryKey: ['me'] });
          void qc.invalidateQueries({ queryKey: ['ledger'] });
          if ((msg.amount ?? 0) > 0) awardRef.current(msg.amount ?? 0);
        }
        if (msg.type === 'rank') {
          void qc.invalidateQueries({ queryKey: ['rank'] });
          void qc.invalidateQueries({ queryKey: ['me'] });
        }
      };
      ws.onclose = (e) => {
        if (stopped) return;
        if (e.code === 4401) useSession.setState({ token: null, expiresAt: 0 });
        timer = setTimeout(open, Math.min(30_000, 1000 * 2 ** retry++));
      };
    };
    void open();
    return () => {
      stopped = true;
      clearTimeout(timer);
      ws?.close();
    };
  }, [qc]);
}

// ── tasks ────────────────────────────────────────────────────────────────────
export type TaskState = 'todo' | 'pending' | 'done' | 'rejected' | 'locked';
export interface TaskCardData {
  key: string;
  group: string;
  title: string;
  description: string | null;
  reward: number;
  rewardForMe: number;
  state: TaskState;
  verification: string;
  rejectReason?: string | null;
  ref?: string;
  detail?: Record<string, unknown>;
}
export interface TaskBoard {
  cards: TaskCardData[];
  vip: boolean;
  multiplier: number;
  channelLink: string | null;
  prizeName: string;
}
export const useTasks = () =>
  useQuery({ queryKey: ['tasks'], queryFn: () => api<TaskBoard>('/api/app/tasks') });

/** Multipart upload (screenshot tasks). */
export async function uploadSubmission(key: string, file: File, fields: Record<string, string>) {
  const t = await token();
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  form.append('file', file);
  const res = await fetch(`/api/app/tasks/${key}/submit`, {
    method: 'POST',
    headers: { authorization: `Bearer ${t}` },
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.error ?? String(res.status));
  return body as { submissionId: number; state: 'pending' };
}

// ── VIP ──────────────────────────────────────────────────────────────────────
export interface VipData {
  isVip: boolean;
  members: number;
  reward: number;
  multiplier: number;
  request: { status: string } | null;
  supportUrl: string | null;
  signal: {
    id: number;
    title: string;
    result: string;
    entry: string | null;
    postedAt: string;
  } | null;
  results: { id: number; title: string; result: string; postedAt: string }[];
  lessons: { id: string; title: string; url: string | null; done: boolean }[];
}
export const useVip = () =>
  useQuery({ queryKey: ['vip'], queryFn: () => api<VipData>('/api/app/vip') });

// ── wheel ────────────────────────────────────────────────────────────────────
export interface WheelSectorView {
  id: number;
  label: string;
  kind: 'physical' | 'cash' | 'tickets' | 'nothing';
  value: number;
  stock: number | null;
  enabled: boolean;
  imageUrl: string | null;
}
export interface Claim {
  id: number;
  prize: string;
  status: string;
}
export interface WheelData {
  wheel: {
    id: number;
    week: number;
    opensAt: string;
    closesAt: string;
    open: boolean;
    scarcityGlow: boolean;
  } | null;
  sectors: WheelSectorView[];
  spins: { base: number; used: number; bonus: number; left: number } | null;
  prizeChancePct?: number;
  history: { id: number; label: string; kind: string; createdAt: string }[];
  winners: {
    id: number;
    name: string;
    city: string | null;
    prize: string;
    mediaUrl: string | null;
  }[];
  claims: Claim[];
}
export interface SpinResult {
  ok: true;
  spinId: number;
  sector: { id: number; label: string; kind: string; value: number };
  awardedTickets: number;
  claimId: number | null;
  streakTickets: number;
}
export const useWheel = () =>
  useQuery({ queryKey: ['wheel'], queryFn: () => api<WheelData>('/api/app/wheel') });

// ── draw ─────────────────────────────────────────────────────────────────────
export interface DrawState {
  id: number;
  type: string;
  title: string;
  prize: string;
  status: string;
  scheduledAt: string | null;
  commitHash: string | null;
  total: number | null;
  liveStartedAt: string | null;
  revealAt: string | null;
  serverNow: string;
  codes: string[];
  winner: {
    name: string;
    city: string | null;
    ticketCode: string;
    ticketIndex: number;
    attempt: number;
  } | null;
  verifiable: boolean;
  forcedTest?: boolean;
}
export const useCurrentDraw = () =>
  useQuery({
    queryKey: ['draw'],
    queryFn: () =>
      api<{ draw: DrawState | null; seasonDrawAt: string; youWon?: boolean }>(
        '/api/app/draw/current',
      ),
    refetchInterval: 30_000,
  });
